import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import api from '../../utils/axiosConfig';
import { showToast } from '../../features/ui/uiSlice';
import { formatLogoUrl } from '../../utils/imageUtils';
import { useDebounce } from '../../hooks/useDebounce';
import {
    BuildingOfficeIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    EyeIcon,
    PencilIcon,
    PlusIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    TrashIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ShieldCheckIcon,
    XMarkIcon,
    EyeSlashIcon
} from '@heroicons/react/24/outline';

interface Company {
    _id: string;
    companyName: string;
    email: string;
    phone: string;
    companyDetails: {
        industry: string;
        companySize: string;
        website?: string;
        description: string;
        registrationNumber: string;
        panNumber: string;
        gstNumber?: string;
        address: {
            street?: string;
            city?: string;
            state?: string;
            pincode?: string;
            country?: string;
        };
    };
    contactPerson: {
        name: string;
        designation?: string;
        email: string;
        phone: string;
    };
    isActive: boolean;
    isVerified: boolean;
    createdAt: string;
    lastLogin?: string;
    logo?: string;
    subscription: {
        plan: string;
        startDate?: string;
        endDate?: string;
        isActive: boolean;
    };
    settings: {
        allowJobPosting: boolean;
        maxJobPosts: number;
        allowTeamManagement: boolean;
        notificationSettings: {
            emailNotifications: boolean;
            smsNotifications: boolean;
        };
    };
    totalJobsPosted: number;
    totalEmployees: number;
}

const Companies: React.FC = () => {
    const dispatch = useAppDispatch();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [industryFilter, setIndustryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState<any[]>([]);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>('');

    // Password visibility state
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);


    const [editForm, setEditForm] = useState({
        companyName: '',
        email: '',
        phone: '',
        logo: '',
        companyDetails: {
            industry: '',
            companySize: '',
            website: '',
            description: '',
            registrationNumber: '',
            panNumber: '',
            gstNumber: '',
            address: {
                street: '',
                city: '',
                state: '',
                pincode: '',
                country: 'India'
            }
        },
        contactPerson: {
            name: '',
            designation: '',
            email: '',
            phone: ''
        },
        isActive: true,
        isVerified: false
    });
    const [createForm, setCreateForm] = useState({
        companyName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        companyDetails: {
            industry: '',
            companySize: '',
            website: '',
            description: '',
            registrationNumber: '',
            panNumber: '',
            gstNumber: '',
            address: {
                street: '',
                city: '',
                state: '',
                pincode: '',
                country: 'India'
            }
        },
        contactPerson: {
            name: '',
            designation: '',
            email: '',
            phone: ''
        },
        isActive: true,
        isVerified: false
    });

    const fetchCompanies = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('page', currentPage.toString());
            params.append('limit', itemsPerPage.toString());

            if (debouncedSearchTerm) params.append('q', debouncedSearchTerm);
            if (industryFilter !== 'all') params.append('industry', industryFilter);
            if (statusFilter !== 'all') {
                if (statusFilter === 'active') params.append('isActive', 'true');
                if (statusFilter === 'inactive') params.append('isActive', 'false');
            }

            const response = await api.get(`/companies?${params.toString()}`);
            setCompanies(response.data.data);
            setPagination(response.data.pagination);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch companies',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, debouncedSearchTerm, industryFilter, statusFilter, dispatch]);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (currentPage === 1) {
                fetchCompanies();
            } else {
                setCurrentPage(1);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, industryFilter, statusFilter, currentPage, fetchCompanies]);

    const handleToggleCompanyStatus = async (companyId: string, currentStatus: boolean) => {
        try {
            await api.put(`/companies/${companyId}`, { isActive: !currentStatus });
            setCompanies(companies.map(company =>
                company._id === companyId ? { ...company, isActive: !currentStatus } : company
            ));
            dispatch(showToast({
                message: `Company ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
                type: 'success'
            }));
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to update company status',
                type: 'error'
            }));
        }
    };

    const handleToggleVerification = async (companyId: string, currentVerified: boolean) => {
        try {
            await api.put(`/companies/${companyId}/verify`);
            setCompanies(companies.map(company =>
                company._id === companyId ? { ...company, isVerified: true } : company
            ));
            dispatch(showToast({
                message: 'Company verified successfully',
                type: 'success'
            }));
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to update company verification',
                type: 'error'
            }));
        }
    };

    const handleViewCompany = (company: Company) => {
        setSelectedCompany(company);
        setShowCompanyModal(true);
    };

    const handleEditCompany = (company: Company) => {
        setSelectedCompany(company);
        setEditForm({
            companyName: company.companyName || '',
            email: company.email || '',
            phone: company.phone || '',
            logo: company.logo || '',
            companyDetails: {
                industry: company.companyDetails?.industry || '',
                companySize: company.companyDetails?.companySize || '',
                website: company.companyDetails?.website || '',
                description: company.companyDetails?.description || '',
                registrationNumber: company.companyDetails?.registrationNumber || '',
                panNumber: company.companyDetails?.panNumber || '',
                gstNumber: company.companyDetails?.gstNumber || '',
                address: {
                    street: company.companyDetails?.address?.street || '',
                    city: company.companyDetails?.address?.city || '',
                    state: company.companyDetails?.address?.state || '',
                    pincode: company.companyDetails?.address?.pincode || '',
                    country: company.companyDetails?.address?.country || 'India'
                }
            },
            contactPerson: {
                name: company.contactPerson?.name || '',
                designation: company.contactPerson?.designation || '',
                email: company.contactPerson?.email || '',
                phone: company.contactPerson?.phone || ''
            },
            isActive: company.isActive,
            isVerified: company.isVerified
        });
        setValidationErrors([]);
        setLogoFile(null);
        setLogoPreview(formatLogoUrl(company.logo) || '');
        setShowEditModal(true);
    };

    const handleCreateCompany = () => {
        setCreateForm({
            companyName: '',
            email: '',
            phone: '',
            password: '',
            confirmPassword: '',
            companyDetails: {
                industry: '',
                companySize: '',
                website: '',
                description: '',
                registrationNumber: '',
                panNumber: '',
                gstNumber: '',
                address: {
                    street: '',
                    city: '',
                    state: '',
                    pincode: '',
                    country: 'India'
                }
            },
            contactPerson: {
                name: '',
                designation: '',
                email: '',
                phone: ''
            },
            isActive: true,
            isVerified: false
        });
        setValidationErrors([]);
        setLogoFile(null);
        setLogoPreview('');
        setShowCreateModal(true);
        setShowPassword(false); // Reset password visibility
        setShowConfirmPassword(false); // Reset confirm password visibility
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCompany) return;

        try {
            let logoUrl = editForm.logo;

            // Upload new logo if file is selected
            if (logoFile) {
                logoUrl = await uploadLogo(logoFile);
            }

            const companyData = {
                ...editForm,
                logo: logoUrl
            };

            const response = await api.put(`/companies/${selectedCompany._id}`, companyData);
            const updatedCompany = response.data.data;
            setCompanies(companies.map(company =>
                company._id === selectedCompany._id ? updatedCompany : company
            ));
            setSelectedCompany(updatedCompany);
            setShowEditModal(false);
            dispatch(showToast({
                message: 'Company updated successfully',
                type: 'success'
            }));
        } catch (error: any) {
            console.error('Company update error:', error.response?.data);

            // Handle validation errors
            if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                setValidationErrors(error.response.data.errors);
                const errorMessages = error.response.data.errors.map((err: any) =>
                    `${err.field}: ${err.message}`
                ).join(', ');

                dispatch(showToast({
                    message: `Validation errors: ${errorMessages}`,
                    type: 'error'
                }));
            } else if (error.response?.data?.message) {
                setValidationErrors([]);
                dispatch(showToast({
                    message: error.response.data.message,
                    type: 'error'
                }));
            } else {
                setValidationErrors([]);
                dispatch(showToast({
                    message: 'Failed to update company',
                    type: 'error'
                }));
            }
        }
    };

    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (createForm.password !== createForm.confirmPassword) {
            dispatch(showToast({
                message: 'Passwords do not match',
                type: 'error'
            }));
            return;
        }

        try {
            let logoUrl = '';

            // Upload logo if file is selected
            if (logoFile) {
                logoUrl = await uploadLogo(logoFile);
            }

            const companyData = {
                ...createForm,
                logo: logoUrl
            };

            const response = await api.post('/companies', companyData);
            setCompanies([response.data.data, ...companies]);
            setShowCreateModal(false);
            dispatch(showToast({
                message: 'Company created successfully',
                type: 'success'
            }));
        } catch (error: any) {
            console.error('Company creation error:', error.response?.data);

            // Handle validation errors
            if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                setValidationErrors(error.response.data.errors);
                const errorMessages = error.response.data.errors.map((err: any) =>
                    `${err.field}: ${err.message}`
                ).join(', ');

                dispatch(showToast({
                    message: `Validation errors: ${errorMessages}`,
                    type: 'error'
                }));
            } else if (error.response?.data?.message) {
                setValidationErrors([]);
                dispatch(showToast({
                    message: error.response.data.message,
                    type: 'error'
                }));
            } else {
                setValidationErrors([]);
                dispatch(showToast({
                    message: 'Failed to create company',
                    type: 'error'
                }));
            }
        }
    };

    const handleDeleteCompany = async (companyId: string) => {
        if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
            return;
        }

        try {
            await api.delete(`/companies/${companyId}`);
            setCompanies(companies.filter(company => company._id !== companyId));
            dispatch(showToast({
                message: 'Company deleted successfully',
                type: 'success'
            }));
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to delete company',
                type: 'error'
            }));
        }
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                dispatch(showToast({
                    message: 'Please select an image file',
                    type: 'error'
                }));
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                dispatch(showToast({
                    message: 'File size must be less than 5MB',
                    type: 'error'
                }));
                return;
            }

            setLogoFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadLogo = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append('logo', file);

        const response = await api.post('/upload/logo', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data.url;
    };

    const filteredCompanies = companies;

    const getStatusBadgeColor = (isActive: boolean) => {
        return isActive
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    };

    const getVerificationBadgeColor = (isVerified: boolean) => {
        return isVerified
            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    };

    const industries = Array.from(new Set(companies.map(c => c.companyDetails?.industry).filter(Boolean)));

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">Company Management</h1>
                        <p className="text-blue-100">
                            Manage and monitor all companies in the system
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0">
                        <button
                            onClick={handleCreateCompany}
                            className="bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-lg hover:bg-white/30 transition-all duration-200 border border-white/30 inline-flex items-center space-x-2"
                        >
                            <PlusIcon className="h-5 w-5" />
                            <span>Add Company</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow duration-200">
                    <div className="flex items-center">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                            <BuildingOfficeIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Companies</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{companies.length}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow duration-200">
                    <div className="flex items-center">
                        <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                            <CheckCircleIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Companies</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {companies.filter(c => c.isActive).length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow duration-200">
                    <div className="flex items-center">
                        <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                            <ShieldCheckIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Verified Companies</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {companies.filter(c => c.isVerified).length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow duration-200">
                    <div className="flex items-center">
                        <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl">
                            <ClockIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Verification</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {companies.filter(c => !c.isVerified).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search companies..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <FunnelIcon className="h-5 w-5 mr-2" />
                        Filters
                    </button>
                </div>

                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Industry
                                </label>
                                <select
                                    value={industryFilter}
                                    onChange={(e) => setIndustryFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="all">All Industries</option>
                                    {industries.map(industry => (
                                        <option key={industry} value={industry}>{industry}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Status
                                </label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Companies Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Logo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Company
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Industry
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Verification
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Joined
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredCompanies.map((company) => (
                                <tr key={company._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center justify-center">
                                            {company.logo ? (
                                                <img
                                                    src={formatLogoUrl(company.logo)}
                                                    alt={`${company.companyName} logo`}
                                                    className="h-12 w-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                        const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                        if (nextElement) {
                                                            nextElement.style.display = 'flex';
                                                        }
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className={`h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center ${company.logo ? 'hidden' : 'flex'}`}
                                            >
                                                <span className="text-lg font-bold text-white">
                                                    {company.companyName?.charAt(0) || 'C'}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {company.companyName}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {company.email}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {company.companyDetails?.industry || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(company.isActive)}`}>
                                            {company.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVerificationBadgeColor(company.isVerified)}`}>
                                            {company.isVerified ? 'Verified' : 'Pending'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(company.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleViewCompany(company)}
                                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                                title="View Details"
                                            >
                                                <EyeIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleEditCompany(company)}
                                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                                title="Edit Company"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleToggleVerification(company._id, company.isVerified)}
                                                className={`${company.isVerified
                                                    ? 'text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300'
                                                    : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                                                    }`}
                                                title={company.isVerified ? 'Unverify' : 'Verify'}
                                            >
                                                {company.isVerified ? <XCircleIcon className="h-5 w-5" /> : <CheckCircleIcon className="h-5 w-5" />}
                                            </button>
                                            <button
                                                onClick={() => handleToggleCompanyStatus(company._id, company.isActive)}
                                                className={`${company.isActive
                                                    ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                                                    : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                                                    }`}
                                                title={company.isActive ? 'Deactivate' : 'Activate'}
                                            >
                                                {company.isActive ? <XCircleIcon className="h-5 w-5" /> : <CheckCircleIcon className="h-5 w-5" />}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCompany(company._id)}
                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                title="Delete Company"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredCompanies.length === 0 && (
                    <div className="text-center py-12">
                        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No companies found</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {searchTerm || industryFilter !== 'all' || statusFilter !== 'all'
                                ? 'Try adjusting your search or filter criteria.'
                                : 'Get started by adding a new company.'
                            }
                        </p>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mt-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                            <span>
                                Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                                {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                                {pagination.totalItems} results
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon className="h-4 w-4" />
                            </button>
                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                const pageNum = i + 1;
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`px-3 py-1 text-sm border rounded-md ${currentPage === pageNum
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === pagination.totalPages}
                                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRightIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Company Modal */}
            {showCompanyModal && selectedCompany && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Company Details</h2>
                                <button
                                    onClick={() => setShowCompanyModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Company Header with Logo */}
                            <div className="flex items-center space-x-6 mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                                <div className="flex-shrink-0">
                                    {selectedCompany.logo ? (
                                        <img
                                            src={formatLogoUrl(selectedCompany.logo)}
                                            alt={`${selectedCompany.companyName} logo`}
                                            className="h-20 w-20 rounded-xl object-cover border-2 border-white dark:border-gray-600 shadow-lg"
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                if (nextElement) {
                                                    nextElement.style.display = 'flex';
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className={`h-20 w-20 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-white dark:border-gray-600 shadow-lg ${selectedCompany.logo ? 'hidden' : 'flex'}`}
                                    >
                                        <span className="text-2xl font-bold text-white">
                                            {selectedCompany.companyName?.charAt(0) || 'C'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                        {selectedCompany.companyName}
                                    </h1>
                                    <p className="text-gray-600 dark:text-gray-300 mb-1">
                                        {selectedCompany.email}
                                    </p>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        {selectedCompany.phone}
                                    </p>
                                    <div className="flex items-center space-x-4 mt-3">
                                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(selectedCompany.isActive)}`}>
                                            {selectedCompany.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${selectedCompany.isVerified ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'}`}>
                                            {selectedCompany.isVerified ? 'Verified' : 'Pending Verification'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Basic Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyName}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.email}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.phone}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Industry</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.industry || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Size</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.companySize || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Website</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.website || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Company Details */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Details</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Registration Number</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.registrationNumber || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">PAN Number</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.panNumber || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">GST Number</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.gstNumber || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.description || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Contact Person */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Person</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.contactPerson?.name || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Designation</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.contactPerson?.designation || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.contactPerson?.email || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.contactPerson?.phone || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Address</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Street</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.address?.street || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.address?.city || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">State</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.address?.state || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pincode</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.address?.pincode || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{selectedCompany.companyDetails?.address?.country || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Status Information */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Status Information</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(selectedCompany.isActive)}`}>
                                            {selectedCompany.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verification</label>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVerificationBadgeColor(selectedCompany.isVerified)}`}>
                                            {selectedCompany.isVerified ? 'Verified' : 'Pending'}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Created At</label>
                                        <p className="text-sm text-gray-900 dark:text-white">{new Date(selectedCompany.createdAt).toLocaleDateString()}</p>
                                    </div>
                                    {selectedCompany.lastLogin && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Login</label>
                                            <p className="text-sm text-gray-900 dark:text-white">{selectedCompany?.lastLogin ? new Date(selectedCompany.lastLogin).toLocaleDateString() : 'N/A'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end mt-6">
                                <button
                                    onClick={() => setShowCompanyModal(false)}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Company Modal */}
            {showEditModal && selectedCompany && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Company</h2>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Validation Errors */}
                            {validationErrors.length > 0 && (
                                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Validation Errors:</h3>
                                    <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                                        {validationErrors.map((error, index) => (
                                            <li key={index} className="flex items-start">
                                                <span className="font-medium mr-2">•</span>
                                                <span>
                                                    <span className="font-medium">{error.field}:</span> {error.message}
                                                    {error.value && <span className="text-gray-500"> (Value: "{error.value}")</span>}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <form onSubmit={handleEditSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Basic Information */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Company Name
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyName}
                                                onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                value={editForm.email}
                                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                value={editForm.phone}
                                                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Company Details */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Details</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Industry
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.industry}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: { ...editForm.companyDetails, industry: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Company Size
                                            </label>
                                            <select
                                                value={editForm.companyDetails.companySize}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: { ...editForm.companyDetails, companySize: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            >
                                                <option value="">Select company size</option>
                                                <option value="1-10">1-10 employees</option>
                                                <option value="11-50">11-50 employees</option>
                                                <option value="51-200">51-200 employees</option>
                                                <option value="201-500">201-500 employees</option>
                                                <option value="500+">500+ employees</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Website
                                            </label>
                                            <input
                                                type="url"
                                                value={editForm.companyDetails.website}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: { ...editForm.companyDetails, website: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Registration Number *
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.registrationNumber}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: { ...editForm.companyDetails, registrationNumber: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                PAN Number *
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.panNumber}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: { ...editForm.companyDetails, panNumber: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                GST Number
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.gstNumber}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: { ...editForm.companyDetails, gstNumber: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Description
                                            </label>
                                            <textarea
                                                rows={3}
                                                value={editForm.companyDetails.description}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: { ...editForm.companyDetails, description: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Person */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Person</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Name
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.contactPerson.name}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    contactPerson: { ...editForm.contactPerson, name: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Designation
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.contactPerson.designation}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    contactPerson: { ...editForm.contactPerson, designation: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                value={editForm.contactPerson.email}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    contactPerson: { ...editForm.contactPerson, email: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                value={editForm.contactPerson.phone}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    contactPerson: { ...editForm.contactPerson, phone: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Address</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Street
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.address.street}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: {
                                                        ...editForm.companyDetails,
                                                        address: { ...editForm.companyDetails.address, street: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.address.city}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: {
                                                        ...editForm.companyDetails,
                                                        address: { ...editForm.companyDetails.address, city: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                State
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.address.state}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: {
                                                        ...editForm.companyDetails,
                                                        address: { ...editForm.companyDetails.address, state: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Pincode
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.address.pincode}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: {
                                                        ...editForm.companyDetails,
                                                        address: { ...editForm.companyDetails.address, pincode: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Country
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.companyDetails.address.country}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    companyDetails: {
                                                        ...editForm.companyDetails,
                                                        address: { ...editForm.companyDetails.address, country: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Logo */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Logo</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Company Logo
                                        </label>
                                        <div className="space-y-4">
                                            {/* Current Logo Preview */}
                                            {logoPreview && (
                                                <div className="flex items-center space-x-4">
                                                    <img
                                                        src={formatLogoUrl(logoPreview)}
                                                        alt="Current logo"
                                                        className="w-20 h-20 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                                                    />
                                                    <div>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">Current logo</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setLogoFile(null);
                                                                setLogoPreview(formatLogoUrl(editForm.logo) || '');
                                                            }}
                                                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* File Upload */}
                                            <div>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleLogoChange}
                                                    className="block w-full text-sm text-gray-500 dark:text-gray-400
                                                        file:mr-4 file:py-2 file:px-4
                                                        file:rounded-lg file:border-0
                                                        file:text-sm file:font-medium
                                                        file:bg-blue-50 file:text-blue-700
                                                        hover:file:bg-blue-100
                                                        dark:file:bg-blue-900/20 dark:file:text-blue-400
                                                        dark:hover:file:bg-blue-900/30"
                                                />
                                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                    PNG, JPG, GIF up to 5MB
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="flex items-center space-x-6">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={editForm.isActive}
                                            onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={editForm.isVerified}
                                            onChange={(e) => setEditForm({ ...editForm, isVerified: e.target.checked })}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Verified</span>
                                    </label>
                                </div>

                                <div className="flex justify-end space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowEditModal(false)}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        Update Company
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Company Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Company</h2>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Validation Errors */}
                            {validationErrors.length > 0 && (
                                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Validation Errors:</h3>
                                    <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                                        {validationErrors.map((error, index) => (
                                            <li key={index} className="flex items-start">
                                                <span className="font-medium mr-2">•</span>
                                                <span>
                                                    <span className="font-medium">{error.field}:</span> {error.message}
                                                    {error.value && <span className="text-gray-500"> (Value: "{error.value}")</span>}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <form onSubmit={handleCreateSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Basic Information */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Company Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyName}
                                                onChange={(e) => setCreateForm({ ...createForm, companyName: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                value={createForm.email}
                                                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Phone *
                                            </label>
                                            <input
                                                type="tel"
                                                value={createForm.phone}
                                                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Password *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    value={createForm.password}
                                                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                    placeholder="Create password"
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                >
                                                    {showPassword ? (
                                                        <EyeSlashIcon className="h-5 w-5" />
                                                    ) : (
                                                        <EyeIcon className="h-5 w-5" />
                                                    )}
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                At least 6 chars with uppercase, lowercase & number
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Confirm Password *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    value={createForm.confirmPassword}
                                                    onChange={(e) => setCreateForm({ ...createForm, confirmPassword: e.target.value })}
                                                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                    placeholder="Confirm password"
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                >
                                                    {showConfirmPassword ? (
                                                        <EyeSlashIcon className="h-5 w-5" />
                                                    ) : (
                                                        <EyeIcon className="h-5 w-5" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Company Details */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Details</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Industry
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.industry}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: { ...createForm.companyDetails, industry: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Company Size
                                            </label>
                                            <select
                                                value={createForm.companyDetails.companySize}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: { ...createForm.companyDetails, companySize: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            >
                                                <option value="">Select company size</option>
                                                <option value="1-10">1-10 employees</option>
                                                <option value="11-50">11-50 employees</option>
                                                <option value="51-200">51-200 employees</option>
                                                <option value="201-500">201-500 employees</option>
                                                <option value="500+">500+ employees</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Website
                                            </label>
                                            <input
                                                type="url"
                                                value={createForm.companyDetails.website}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: { ...createForm.companyDetails, website: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Registration Number *
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.registrationNumber}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: { ...createForm.companyDetails, registrationNumber: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                PAN Number *
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.panNumber}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: { ...createForm.companyDetails, panNumber: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                GST Number
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.gstNumber}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: { ...createForm.companyDetails, gstNumber: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Description
                                            </label>
                                            <textarea
                                                rows={3}
                                                value={createForm.companyDetails.description}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: { ...createForm.companyDetails, description: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Person */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Person</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.contactPerson.name}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    contactPerson: { ...createForm.contactPerson, name: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Designation
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.contactPerson.designation}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    contactPerson: { ...createForm.contactPerson, designation: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                value={createForm.contactPerson.email}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    contactPerson: { ...createForm.contactPerson, email: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Phone *
                                            </label>
                                            <input
                                                type="tel"
                                                value={createForm.contactPerson.phone}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    contactPerson: { ...createForm.contactPerson, phone: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Address</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Street
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.address.street}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: {
                                                        ...createForm.companyDetails,
                                                        address: { ...createForm.companyDetails.address, street: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.address.city}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: {
                                                        ...createForm.companyDetails,
                                                        address: { ...createForm.companyDetails.address, city: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                State
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.address.state}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: {
                                                        ...createForm.companyDetails,
                                                        address: { ...createForm.companyDetails.address, state: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Pincode
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.address.pincode}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: {
                                                        ...createForm.companyDetails,
                                                        address: { ...createForm.companyDetails.address, pincode: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Country
                                            </label>
                                            <input
                                                type="text"
                                                value={createForm.companyDetails.address.country}
                                                onChange={(e) => setCreateForm({
                                                    ...createForm,
                                                    companyDetails: {
                                                        ...createForm.companyDetails,
                                                        address: { ...createForm.companyDetails.address, country: e.target.value }
                                                    }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Logo */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Logo</h3>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Company Logo
                                        </label>
                                        <div className="space-y-4">
                                            {/* Logo Preview */}
                                            {logoPreview && (
                                                <div className="flex items-center space-x-4">
                                                    <img
                                                        src={formatLogoUrl(logoPreview)}
                                                        alt="Logo preview"
                                                        className="w-20 h-20 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                                                    />
                                                    <div>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">Selected logo</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setLogoFile(null);
                                                                setLogoPreview('');
                                                            }}
                                                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* File Upload */}
                                            <div>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleLogoChange}
                                                    className="block w-full text-sm text-gray-500 dark:text-gray-400
                                                        file:mr-4 file:py-2 file:px-4
                                                        file:rounded-lg file:border-0
                                                        file:text-sm file:font-medium
                                                        file:bg-blue-50 file:text-blue-700
                                                        hover:file:bg-blue-100
                                                        dark:file:bg-blue-900/20 dark:file:text-blue-400
                                                        dark:hover:file:bg-blue-900/30"
                                                />
                                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                    PNG, JPG, GIF up to 5MB
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="flex items-center space-x-6">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={createForm.isActive}
                                            onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.checked })}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={createForm.isVerified}
                                            onChange={(e) => setCreateForm({ ...createForm, isVerified: e.target.checked })}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Verified</span>
                                    </label>
                                </div>

                                <div className="flex justify-end space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateModal(false)}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        Create Company
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Companies;
